import React, { useState, useRef, useEffect } from "react";
import {
    getBookingQuestions,
    dogbreedOptionsMap,
    catbreedOptionsMap,
    petOptions,
} from "../../../utils/Customer/CustomerData";
import PackageCard from "../../../components/customer/PackageCard";
import { useLocation, useNavigate } from "react-router-dom";
import SelectPetsSection from "../../../components/customer/SelectPetsSection";
import QuestionCard, {
    QuestionType,
} from "../../../components/customer/QuestionCard";
import { useAlert } from "../../../components/customer/AlertContext";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import WellnessBanner from "../../../components/customer/PetSectionComponents.tsx/WellnessBanner";
import {
    useGetFilteredPackagesMutation,
} from "../../../redux/actions/Customer/serviceCustomer";
import {
    FilteredPackage,
    PetData,
} from "../../../types/Response/CustomerResponse";
import {
    useCreateUserProfileMutation,
    useLazyGetPetProfileQuery,
    useUploadCustomerFileMutation,
} from "../../../redux/actions/Customer/profileCustomer";
import { ICreatePetProfileRequest } from "../../../types/Request/CustomerRequest";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/rootReducer";

// Cartoon Avatars
const DOG_CARTOON_AVATARS = [
    {
        id: "dog-1",
        label: "Happy Pup",
        value:
            "https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=300&auto=format&fit=crop&q=80",
    },
    {
        id: "dog-2",
        label: "Playful Beagle",
        value:
            "https://images.unsplash.com/photo-1537151608828-ea2b11777ee8?w=300&auto=format&fit=crop&q=80",
    },
    {
        id: "dog-3",
        label: "Cute Golden",
        value:
            "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=300&auto=format&fit=crop&q=80",
    },
    {
        id: "dog-4",
        label: "Fluffy Corgi",
        value:
            "https://images.unsplash.com/photo-1612774412771-005ed8e861d2?w=300&auto=format&fit=crop&q=80",
    },
];

const CAT_CARTOON_AVATARS = [
    {
        id: "cat-1",
        label: "Cute Kitty",
        value:
            "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=300&auto=format&fit=crop&q=80",
    },
    {
        id: "cat-2",
        label: "Playful Cat",
        value:
            "https://images.unsplash.com/photo-1573865526739-10659fec78a5?w=300&auto=format&fit=crop&q=80",
    },
    {
        id: "cat-3",
        label: "Fluffy Ginger",
        value:
            "https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=300&auto=format&fit=crop&q=80",
    },
    {
        id: "cat-4",
        label: "Sleepy Kitty",
        value:
            "https://images.unsplash.com/photo-1561948955-570b270e7c36?w=300&auto=format&fit=crop&q=80",
    },
];

/**
 * The UI currently stores the selected size option
 * inside answers.weight.
 *
 * IMPORTANT:
 * This mapping is NOT based on numeric weight.
 *
 * It directly maps the selected option ID to breedSize.
 */
const BREED_SIZE_MAP: Record<string, string> = {
    toy: "toy",
    small: "small",
    medium: "medium",
    large: "large",
    xl: "Extra Large",
};

const GroomingBookingService: React.FC = () => {
    const { showConfirm } = useAlert();
    const navigate = useNavigate();

    const isLoggedIn = useSelector(
        (state: RootState) => state.auth.customer.isLoggedIn
    );

    const location = useLocation();

    const SERVICE_ID = location.state.id;

    // Redux Actions
    const [filteredPackages, setFilteredPackages] = useState<
        FilteredPackage[]
    >([]);

    const [isLoadingPackages, setIsLoadingPackages] = useState(false);

    const [getFilterPackage] = useGetFilteredPackagesMutation();

    const [
        triggerGetPetProfile,
        { isLoading: isLoadingPets },
    ] = useLazyGetPetProfileQuery();

    const [
        createUserProfile,
        { isLoading: isCreatingPet },
    ] = useCreateUserProfileMutation();

    const [
        uploadFile,
        { isLoading: isUploadingImage },
    ] = useUploadCustomerFileMutation();

    // UI Anchors
    const standardSliderRef = useRef<HTMLDivElement>(null);
    const questionCardRef = useRef<HTMLDivElement>(null);

    // Stepper & Selection State
    const [currentStep, setCurrentStep] = useState<number>(() => {
        if (localStorage.getItem("selected_active_pet")) return 2;

        if (localStorage.getItem("selected_pet_category")) return 1;

        return 0;
    });

    const [selectedPetCategory, setSelectedPetCategory] = useState<
        string | undefined
    >(() => {
        return (
            localStorage.getItem("selected_pet_category") || undefined
        );
    });

    const [selectedSavedPet, setSelectedSavedPet] = useState<
        PetData | undefined
    >(() => {
        const savedActive =
            localStorage.getItem("selected_active_pet");

        return savedActive
            ? JSON.parse(savedActive)
            : undefined;
    });

    const [questionStep, setQuestionStep] = useState(0);

    const [showQuestionCard, setShowQuestionCard] = useState(false);

    const [pets, setPets] = useState<PetData[]>([]);

    const [answers, setAnswers] = useState<{
        petType: string;
        petName: string;
        breed: string;
        gender: string;

        /**
         * NOTE:
         * This field contains the SIZE option ID:
         *
         * toy
         * small
         * medium
         * large
         * xl
         *
         * It is NOT converted to determine breedSize.
         */
        weight: string;

        petImage: string | File;
    }>({
        petType: "",
        petName: "",
        breed: "",
        gender: "",
        weight: "",
        petImage: "",
    });

    // ---------------------------------------------------------
    // Fetch Pets
    // ---------------------------------------------------------

    const fetchPetProfiles = async () => {
        try {
            const response =
                await triggerGetPetProfile().unwrap();

            if (response && response.data) {
                setPets(response.data);
            }
        } catch (error) {
            console.error(
                "Failed to fetch pet profiles:",
                error
            );

            setPets([]);
        }
    };

    useEffect(() => {
        fetchPetProfiles();
    }, []);

    // ---------------------------------------------------------
    // Questions
    // ---------------------------------------------------------

    const rawQuestions = getBookingQuestions(
        answers.petType
    );

    const bookingQuestions = rawQuestions.filter(
        (q) =>
            !(
                answers.petType === "cat" &&
                q.key === "weight"
            )
    );

    const standardPackages = filteredPackages;

    // ---------------------------------------------------------
    // Fetch Packages
    // ---------------------------------------------------------

    useEffect(() => {
        const fetchPackages = async () => {
            if (!selectedSavedPet?.id) return;

            try {
                setIsLoadingPackages(true);

                const response =
                    await getFilterPackage({
                        serviceId: SERVICE_ID,
                        petProfileId: selectedSavedPet.id,
                    }).unwrap();

                setFilteredPackages(response || []);
            } catch (error) {
                console.error(
                    "Package API Error:",
                    error
                );

                setFilteredPackages([]);
            } finally {
                setIsLoadingPackages(false);
            }
        };

        fetchPackages();
    }, [selectedSavedPet, getFilterPackage]);

    // ---------------------------------------------------------
    // Filter Pets By Category
    // ---------------------------------------------------------

    const profilesFilteredByCategory = pets
        .filter((pet) => {
            if (!selectedPetCategory) return true;

            const selectedType = String(
                selectedPetCategory
            )
                .trim()
                .toLowerCase();

            const petType =
                Number(pet.petType) === 2
                    ? "cat"
                    : Number(pet.petType) === 1
                        ? "dog"
                        : String(pet.petType || "")
                            .trim()
                            .toLowerCase();

            return petType === selectedType;
        })
        .map((pet) => {
            const isCat =
                Number(pet.petType) === 2 ||
                String(pet.petType).toLowerCase() ===
                "cat";

            return {
                ...pet,

                id: pet.id,

                petName: pet.petName,

                name: pet.petName,

                petType: isCat ? "cat" : "dog",

                photoUrl:
                    pet.photoUrl ||
                    (isCat
                        ? CAT_CARTOON_AVATARS[0].value
                        : DOG_CARTOON_AVATARS[0].value),

                image: pet.photoUrl,

                /**
                 * Preserve the backend breedSize.
                 *
                 * DO NOT calculate it from weight.
                 */
                breedSize:
                    pet.breedSize || "standard",
            };
        });

    // ---------------------------------------------------------
    // Pet Category Select
    // ---------------------------------------------------------

    const handleCategorySelect = (
        categoryItem: any
    ) => {
        const categoryValue =
            categoryItem?.value ||
            categoryItem?.id;

        setSelectedPetCategory(categoryValue);

        setSelectedSavedPet(undefined);

        setShowQuestionCard(false);

        if (categoryValue) {
            localStorage.setItem(
                "selected_pet_category",
                categoryValue
            );

            setCurrentStep(1);
        } else {
            localStorage.removeItem(
                "selected_pet_category"
            );
        }

        localStorage.removeItem(
            "selected_active_pet"
        );

        window.scrollTo({
            top: 0,
            behavior: "auto",
        });
    };

    // ---------------------------------------------------------
    // Saved Pet Select
    // ---------------------------------------------------------

    const handleSavedPetSelect = (
        pet: any
    ) => {
        const originalPet =
            pets.find(
                (p) => p.id === pet.id
            ) || pet;

        setSelectedSavedPet(originalPet);

        localStorage.setItem(
            "selected_active_pet",
            JSON.stringify(originalPet)
        );

        setCurrentStep(2);

        window.scrollTo({
            top: 0,
            behavior: "auto",
        });
    };

    // ---------------------------------------------------------
    // Step Navigation
    // ---------------------------------------------------------

    const handleStepHeaderNavigation = (
        targetStep: number
    ) => {
        if (targetStep === 0) {
            setSelectedPetCategory(undefined);

            setSelectedSavedPet(undefined);

            setShowQuestionCard(false);

            localStorage.removeItem(
                "selected_pet_category"
            );

            localStorage.removeItem(
                "selected_active_pet"
            );

            setCurrentStep(0);
        } else if (
            targetStep === 1 &&
            selectedPetCategory
        ) {
            setShowQuestionCard(false);

            setCurrentStep(1);
        }

        window.scrollTo({
            top: 0,
            behavior: "auto",
        });
    };

    // ---------------------------------------------------------
    // Answer Handler
    // ---------------------------------------------------------

    const handleAnswer = (value: any) => {
        const currentQuestion =
            bookingQuestions[questionStep];

        if (!currentQuestion) return;

        setAnswers((prev) => ({
            ...prev,
            [currentQuestion.key]: value,
        }));
    };

    // ---------------------------------------------------------
    // Continue / Create Pet
    // ---------------------------------------------------------

    const handleContinue = async () => {
        if (
            questionStep <
            bookingQuestions.length - 1
        ) {
            setQuestionStep(
                (prev) => prev + 1
            );

            return;
        }

        try {
            const activePetType =
                answers.petType ||
                selectedPetCategory ||
                "dog";

            // -------------------------------------------------
            // Photo
            // -------------------------------------------------

            let finalPhotoUrl =
                activePetType === "cat"
                    ? CAT_CARTOON_AVATARS[0].value
                    : DOG_CARTOON_AVATARS[0].value;

            if (
                answers.petImage instanceof File
            ) {
                const uploadRes =
                    await uploadFile({
                        File: answers.petImage,
                        FolderName:
                            "CMS/EditorImages",
                    }).unwrap();

                if (uploadRes) {
                    finalPhotoUrl =
                        typeof uploadRes ===
                            "string"
                            ? uploadRes
                            : (uploadRes as any)
                                .fileUrl ||
                            (uploadRes as any)
                                .url ||
                            finalPhotoUrl;
                }
            } else if (
                typeof answers.petImage ===
                "string" &&
                answers.petImage.trim() !== ""
            ) {
                finalPhotoUrl =
                    answers.petImage;
            }

            // -------------------------------------------------
            // Pet Type
            // -------------------------------------------------

            const petTypeNumeric =
                activePetType === "cat"
                    ? 2
                    : 1;

            // -------------------------------------------------
            // Gender
            //
            // Your QuestionCard uses:
            // 1 = Male
            // 2 = Female
            // -------------------------------------------------

            const genderNumeric =
                answers.gender === "2"
                    ? 2
                    : 1;

            // -------------------------------------------------
            // BREED SIZE
            //
            // IMPORTANT:
            // We DO NOT calculate this from weight.
            //
            // answers.weight is actually the selected
            // size option:
            //
            // toy
            // small
            // medium
            // large
            // xl
            //
            // Therefore we directly map the selected option.
            // -------------------------------------------------

            const breedSize =
                BREED_SIZE_MAP[
                answers.weight
                ] || "medium";

            // -------------------------------------------------
            // Numeric Weight
            //
            // Since you don't want breedSize to be determined
            // from weight, there is NO parseFloat here.
            //
            // The current UI does not collect an exact numeric
            // weight, so we don't use it for breedSize.
            //
            // If your backend requires this field, keep the
            // existing default.
            // -------------------------------------------------

            const weightNumeric = 0;

            // -------------------------------------------------
            // Payload
            // -------------------------------------------------

            const payload: ICreatePetProfileRequest =
            {
                currentSubmittedStep: 1,

                isFullyFilled: true,

                petName:
                    answers.petName ||
                    "Pet",

                petType:
                    petTypeNumeric,

                breed:
                    answers.breed ||
                    "Standard",

                gender:
                    genderNumeric,

                weight:
                    weightNumeric,

                dateType: "DOB",

                petDate:
                    new Date().toISOString(),

                furColor: "Standard",

                activityLevel:
                    "Medium",

                personality: [],

                foodPreferences: [],

                dietFrequency:
                    "Twice daily",

                portionSize:
                    "Medium",

                favouriteTreats: [],

                noKnownIssues: true,

                healthConditions: [],

                requiresVetConsultationAlert:
                    false,

                groomingFrequency:
                    "Monthly",

                sheddingFrequency:
                    "Moderate",

                /**
                 * DIRECT SIZE SELECTION
                 *
                 * Large selected  -> large
                 * Giant selected   -> giant
                 */
                breedSize:
                    breedSize,

                photoUrl:
                    finalPhotoUrl,
            };

            console.log(
                "CREATE PET PAYLOAD:",
                payload
            );

            // -------------------------------------------------
            // Create Pet
            // -------------------------------------------------

            const response =
                await createUserProfile(
                    payload
                ).unwrap();

            if (
                response?.success &&
                response?.data
            ) {
                const createdPet =
                    response.data;

                // Refresh pets
                await fetchPetProfiles();

                // Select newly created pet
                setSelectedSavedPet(
                    createdPet
                );

                setShowQuestionCard(
                    false
                );

                localStorage.setItem(
                    "selected_active_pet",
                    JSON.stringify(
                        createdPet
                    )
                );

                if (answers.petType) {
                    localStorage.setItem(
                        "selected_pet_category",
                        answers.petType
                    );

                    setSelectedPetCategory(
                        answers.petType
                    );
                }

                // Reset form
                setAnswers({
                    petType: "",
                    petName: "",
                    breed: "",
                    gender: "",
                    weight: "",
                    petImage: "",
                });

                setQuestionStep(0);

                setCurrentStep(2);

                window.scrollTo({
                    top: 0,
                    behavior: "auto",
                });
            }
        } catch (error) {
            console.error(
                "Failed to create pet profile:",
                error
            );

            if (isLoggedIn) {
                showConfirm({
                    title: "Error",

                    message:
                        "Failed to create pet profile. Please try again.",

                    type: "error",

                    confirmText: "Close",

                    showCancel: false,
                });
            }
        }
    };

    // ---------------------------------------------------------
    // Current Question
    // ---------------------------------------------------------

    const currentQuestion =
        bookingQuestions[questionStep];

    let currentOptions =
        currentQuestion?.options || [];

    const activePetType =
        answers.petType ||
        selectedPetCategory ||
        "dog";

    if (
        currentQuestion?.key === "breed"
    ) {
        currentOptions =
            activePetType === "dog"
                ? dogbreedOptionsMap
                : catbreedOptionsMap;
    } else if (
        currentQuestion?.key === "petImage"
    ) {
        currentOptions =
            activePetType === "cat"
                ? CAT_CARTOON_AVATARS
                : DOG_CARTOON_AVATARS;
    }

    // ---------------------------------------------------------
    // Add Pet
    // ---------------------------------------------------------

    const handleAddPetClick = () => {
        setShowQuestionCard(true);

        setQuestionStep(0);

        setAnswers((prev) => ({
            ...prev,

            petType:
                selectedPetCategory || "",
        }));

        setTimeout(() => {
            questionCardRef.current?.scrollIntoView(
                {
                    behavior: "smooth",
                    block: "center",
                }
            );
        }, 150);
    };

    // ---------------------------------------------------------
    // Package Slider
    // ---------------------------------------------------------

    const scrollSlider = (
        ref: React.RefObject<
            HTMLDivElement | null
        >,
        direction: "left" | "right"
    ) => {
        if (ref.current) {
            const clientWidth =
                ref.current.clientWidth;

            const scrollAmount =
                direction === "left"
                    ? -clientWidth
                    : clientWidth;

            ref.current.scrollBy({
                left: scrollAmount,
                behavior: "smooth",
            });
        }
    };

    // ---------------------------------------------------------
    // Steps
    // ---------------------------------------------------------

    const stepsData = [
        {
            label: "Pet Type",
        },
        {
            label: "Pet Profile",
        },
        {
            label: "Service Package",
        },
    ];

    // ---------------------------------------------------------
    // Package Selection
    // ---------------------------------------------------------

    const handleSelectPackageAndNavigate = (
        item: FilteredPackage
    ) => {
        navigate(
            "/customergroomingbookingdatescreen/addonscreen",
            {
                state: {
                    serviceId: SERVICE_ID,

                    serviceType: "grooming",

                    petProfileId:
                        selectedSavedPet?.id,

                    selectedPackage: {
                        packageId:
                            item.packageId,

                        packageTitle:
                            item.packageTitle,

                        packageSubtitle:
                            item.packageSubtitle,

                        description:
                            item.description,

                        includes:
                            item.includes,

                        pricing: {
                            pricingId:
                                item.pricingId,

                            label:
                                item.pricingLabel ||
                                "",

                            price:
                                item.originalPrice ||
                                0,

                            salePrice:
                                item.salePrice ||
                                0,
                        },

                        petProfileId:
                            selectedSavedPet?.id,

                        time:
                            item.durationMinutes,
                    },
                },
            }
        );
    };

    // ---------------------------------------------------------
    // UI
    // ---------------------------------------------------------

    return (
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 relative z-30 pt-4 sm:pt-6 space-y-6 sm:space-y-10 overflow-x-hidden">

            {/* STEPPER HEADER */}

            <div className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-xl shadow-gray-100/40 border border-gray-100 mb-4 sm:mb-8 max-w-5xl mx-auto">

                <div className="flex items-center justify-between gap-1 sm:gap-2">

                    {stepsData.map(
                        (stepItem, idx) => {
                            const isCompleted =
                                currentStep >
                                idx;

                            const isActive =
                                currentStep ===
                                idx;

                            const isClickable =
                                idx <
                                currentStep;

                            return (
                                <React.Fragment
                                    key={idx}
                                >
                                    <div
                                        onClick={() =>
                                            isClickable &&
                                            handleStepHeaderNavigation(
                                                idx
                                            )
                                        }
                                        className={`flex items-center gap-1.5 sm:gap-3 select-none flex-1 sm:flex-initial justify-center sm:justify-start ${isClickable
                                                ? "cursor-pointer group"
                                                : "cursor-default"
                                            }`}
                                    >
                                        <div
                                            className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm shrink-0 transition-all duration-300 ${isCompleted
                                                    ? "bg-emerald-500 text-white"
                                                    : isActive
                                                        ? "bg-mainBrandColor text-white ring-2 sm:ring-4 ring-orange-100"
                                                        : "bg-gray-100 text-gray-400"
                                                }`}
                                        >
                                            {isCompleted ? (
                                                <Check
                                                    size={
                                                        14
                                                    }
                                                    className="sm:w-4 sm:h-4"
                                                    strokeWidth={
                                                        3
                                                    }
                                                />
                                            ) : (
                                                idx +
                                                1
                                            )}
                                        </div>

                                        <div className="min-w-0">
                                            <h4
                                                className={`text-[11px] sm:text-sm font-semibold truncate transition-colors ${isActive
                                                        ? "text-mainBrandColor"
                                                        : isCompleted
                                                            ? "text-gray-800 group-hover:text-mainBrandColor"
                                                            : "text-gray-400"
                                                    }`}
                                            >
                                                {
                                                    stepItem.label
                                                }
                                            </h4>
                                        </div>
                                    </div>

                                    {idx <
                                        2 && (
                                            <div
                                                className={`h-[2px] flex-1 mx-1 sm:mx-4 rounded-full transition-colors duration-500 ${currentStep >
                                                        idx
                                                        ? "bg-emerald-500"
                                                        : "bg-gray-100"
                                                    }`}
                                            />
                                        )}
                                </React.Fragment>
                            );
                        }
                    )}

                </div>
            </div>

            {/* STEP CONTENT */}

            <div className="w-full">

                {/* STEP 0 */}

                {currentStep ===
                    0 && (
                        <div className="w-full px-1 sm:px-2">

                            <div className="bg-white flex items-center justify-center rounded-2xl sm:rounded-3xl px-3 sm:px-6 py-4 sm:py-5 shadow-xl shadow-gray-100/40 border border-gray-100 w-full">

                                <SelectPetsSection
                                    title="Select Pet Type Category"
                                    subtitle="Choose the companion"
                                    data={petOptions.filter((pet: any) => {
                                        const value = String(
                                            pet.value || pet.id || ""
                                        )
                                            .trim()
                                            .toLowerCase();

                                        // Pet Taxi: Dog only
                                        if (SERVICE_ID === 28) {
                                            return value === "dog";
                                        }

                                        // Other services: Dog + Cat
                                        return value === "dog" || value === "cat";
                                    })}
                                    selectedId={
                                        selectedPetCategory
                                    }
                                    onSelect={
                                        handleCategorySelect
                                    }
                                    showAddButton={
                                        false
                                    }
                                    textContainer="flex items-center justify-center"
                                />

                            </div>
                        </div>
                    )}

                {/* STEP 1 */}

                {currentStep ===
                    1 && (
                        <div className="w-full px-1 sm:px-2 space-y-4 sm:space-y-6">

                            {selectedPetCategory &&
                                !showQuestionCard && (
                                    <div className="bg-white flex items-center justify-center rounded-2xl sm:rounded-3xl px-3 sm:px-6 py-4 sm:py-5 shadow-xl shadow-gray-100/40 border border-gray-100">

                                        {isLoadingPets ? (
                                            <div className="py-8 sm:py-10 text-center text-gray-400 text-sm">
                                                Loading your pet profiles...
                                            </div>
                                        ) : (
                                            <SelectPetsSection
                                                title="Who's Getting Pampered?"
                                                subtitle=""
                                                data={
                                                    profilesFilteredByCategory
                                                }
                                                selectedId={
                                                    selectedSavedPet?.id
                                                }
                                                onSelect={
                                                    handleSavedPetSelect
                                                }
                                                showAddButton={
                                                    true
                                                }
                                                onAddClick={
                                                    handleAddPetClick
                                                }
                                                textContainer="flex items-center justify-center"
                                            />
                                        )}

                                    </div>
                                )}

                            <div
                                ref={
                                    questionCardRef
                                }
                                className="scroll-mt-4 sm:scroll-mt-6"
                            >
                                {showQuestionCard &&
                                    bookingQuestions[
                                    questionStep
                                    ] && (
                                        <QuestionCard
                                            currentStep={
                                                questionStep +
                                                1
                                            }
                                            totalSteps={
                                                bookingQuestions.length
                                            }
                                            question={
                                                bookingQuestions[
                                                    questionStep
                                                ].question
                                            }
                                            type={
                                                bookingQuestions[
                                                    questionStep
                                                ].type as QuestionType
                                            }
                                            options={
                                                currentOptions
                                            }
                                            value={
                                                answers[
                                                bookingQuestions[
                                                    questionStep
                                                ].key as keyof typeof answers
                                                ] as any
                                            }
                                            onChange={
                                                handleAnswer
                                            }
                                            onContinue={
                                                handleContinue
                                            }
                                            disabled={
                                                isCreatingPet ||
                                                isUploadingImage
                                            }
                                            onBack={() => {
                                                if (
                                                    questionStep >
                                                    0
                                                ) {
                                                    setQuestionStep(
                                                        (
                                                            prev
                                                        ) =>
                                                            prev -
                                                            1
                                                    );
                                                } else {
                                                    setShowQuestionCard(
                                                        false
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                            </div>

                        </div>
                    )}

                {/* STEP 2 */}

                {currentStep ===
                    2 && (
                        <div className="w-full px-1 sm:px-2 space-y-8 sm:space-y-12">

                            <div className="flex flex-col space-y-4 sm:space-y-6">

                                <div className="space-y-4">

                                    <div className="text-center max-w-md mx-auto px-2">

                                        <h2 className="text-lg sm:text-2xl font-bold text-logoHeading tracking-tight">
                                            Available {SERVICE_ID === 26 ? "Grooming":"Training"}{" "}
                                            Packages
                                        </h2>

                                       
                                    </div>

                                    {isLoadingPackages ? (
                                        <div className="text-center py-8 sm:py-10 text-gray-500 text-sm">
                                            Loading packages...
                                        </div>
                                    ) : standardPackages.length >
                                        0 ? (
                                        <div className="relative w-full">

                                            <button
                                                onClick={() =>
                                                    scrollSlider(
                                                        standardSliderRef,
                                                        "left"
                                                    )
                                                }
                                                className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-white/95 hover:bg-white text-gray-700 hover:text-mainBrandColor p-2 rounded-full shadow-xl border border-gray-100 transition-all duration-300 hover:scale-110 cursor-pointer"
                                                aria-label="Scroll left"
                                            >
                                                <ChevronLeft
                                                    size={
                                                        16
                                                    }
                                                    className="sm:w-6 sm:h-6"
                                                    strokeWidth={
                                                        2.5
                                                    }
                                                />
                                            </button>

                                            <div
                                                ref={
                                                    standardSliderRef
                                                }
                                                className="flex flex-row items-stretch overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden w-full pt-2 sm:pt-4 px-2 sm:px-8 touch-pan-x gap-3 sm:gap-4"
                                            >
                                                {standardPackages.map(
                                                    (
                                                        item: FilteredPackage,
                                                        index: number
                                                    ) => (
                                                        <div
                                                            key={
                                                                item.packageId ||
                                                                index
                                                            }
                                                            className="w-[80vw] max-w-[320px] sm:w-[320px] shrink-0 snap-center sm:snap-start"
                                                        >
                                                            <PackageCard
                                                                packageId={
                                                                    item.packageId
                                                                }
                                                                packageTitle={
                                                                    item.packageTitle
                                                                }
                                                                packageSubtitle={
                                                                    item.packageSubtitle
                                                                }
                                                                description={
                                                                    item.description
                                                                }
                                                                includes={
                                                                    item.includes
                                                                }
                                                                includedBookings={
                                                                    item.includedBookings
                                                                }
                                                                pricing={{
                                                                    pricingId:
                                                                        item.pricingId,

                                                                    /**
                                                                     * IMPORTANT:
                                                                     * This now uses the breedSize
                                                                     * directly saved on the pet.
                                                                     */
                                                                    size:
                                                                        selectedSavedPet?.breedSize ||
                                                                        "standard",

                                                                    label:
                                                                        item.pricingLabel ||
                                                                        "",

                                                                    price:
                                                                        item.originalPrice ||
                                                                        0,

                                                                    salePrice:
                                                                        item.salePrice ||
                                                                        0,
                                                                }}
                                                                handleSelectPackage={() =>
                                                                    handleSelectPackageAndNavigate(
                                                                        item
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    )
                                                )}
                                            </div>

                                            <button
                                                onClick={() =>
                                                    scrollSlider(
                                                        standardSliderRef,
                                                        "right"
                                                    )
                                                }
                                                className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-white/95 hover:bg-white text-gray-700 hover:text-mainBrandColor p-2 rounded-full shadow-xl border border-gray-100 transition-all duration-300 hover:scale-110 cursor-pointer"
                                                aria-label="Scroll right"
                                            >
                                                <ChevronRight
                                                    size={
                                                        16
                                                    }
                                                    className="sm:w-6 sm:h-6"
                                                    strokeWidth={
                                                        2.5
                                                    }
                                                />
                                            </button>

                                        </div>
                                    ) : (
                                        <div className="text-center py-8 sm:py-10 bg-white rounded-2xl sm:rounded-3xl border border-dashed border-gray-200 text-gray-400 text-xs sm:text-sm mx-2 sm:mx-4">
                                            No matching standard
                                            options listed for this
                                            type setup.
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    )}

            </div>

            {/* WELLNESS BANNER */}

            <div className="pt-2 sm:pt-5">

                <WellnessBanner
                    title="Annual UAE"
                    highlightText="Wellness Pass"
                    description="Everything your pet needs to thrive in Dubai’s climate, from hydration checks to heat-stroke prevention."
                    price="AED 4,000"
                    savingsText="Save 800 AED annually"
                    image="https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?w=1200&q=80"
                />

            </div>

        </div>
    );
};

export default GroomingBookingService;